<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { startOfMonth } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { useFlowBuilderStore } from 'flow_builder/store/index.js';
import { useAccount } from 'dashboard/composables/useAccount';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import OverviewTab from 'flow_builder/components/reports/OverviewTab.vue';
import DeliveryTab from 'flow_builder/components/reports/DeliveryTab.vue';
import EngagementTab from 'flow_builder/components/reports/EngagementTab.vue';
import ConversionTab from 'flow_builder/components/reports/ConversionTab.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const store = useFlowBuilderStore();

const flowId = computed(() => Number(route.params.flowId));
const flowName = computed(() => store.currentFlow?.name ?? '');
const customDateRange = ref([startOfMonth(new Date()), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.MONTH_TO_DATE);
const from = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const to = computed(() => getUnixEndOfDay(customDateRange.value[1]));
const activeTab = ref('overview');

const TABS = computed(() => [
  { key: 'overview', name: t('FLOW_BUILDER.REPORTS.TABS.OVERVIEW') },
  { key: 'delivery', name: t('FLOW_BUILDER.REPORTS.TABS.DELIVERY') },
  { key: 'engagement', name: t('FLOW_BUILDER.REPORTS.TABS.ENGAGEMENT') },
  { key: 'conversion', name: t('FLOW_BUILDER.REPORTS.TABS.CONVERSION') },
]);

const selectedTabIndex = computed(() =>
  Math.max(
    TABS.value.findIndex(tab => tab.key === activeTab.value),
    0
  )
);

function onTabChange(index) {
  activeTab.value = TABS.value[index].key;
}

const reportParams = computed(() => ({
  from: new Date(from.value * 1000).toISOString(),
  to: new Date(to.value * 1000).toISOString(),
}));

function fetchAll() {
  store.fetchFlow(flowId.value);
  store.fetchReports(flowId.value, reportParams.value);
}

onMounted(() => {
  // Invalida o report cacheado de outro fluxo para que o skeleton apareça
  // enquanto a nova carga inicial está em voo. Date range trocas mantêm os
  // dados atuais e só atualizam o background loader.
  store.report = null;
  fetchAll();
});

function onDateRangeChange(value) {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  store.report = null;
  store.fetchReports(flowId.value, reportParams.value);
}

function onBreadcrumbClick(_item, index) {
  // index 0 = "Fluxos" → list; index 1 = flow name → editor.
  // Last index (Relatórios) is rendered as plain text and never clickable.
  const routeName = index === 0 ? 'flow_list' : 'flow_editor';
  const params = index === 0 ? {} : { flowId: flowId.value };
  router.push(accountScopedRoute(routeName, params));
}

const activeComponent = computed(() => {
  switch (activeTab.value) {
    case 'delivery':
      return DeliveryTab;
    case 'engagement':
      return EngagementTab;
    case 'conversion':
      return ConversionTab;
    default:
      return OverviewTab;
  }
});
</script>

<template>
  <section class="flex flex-col w-full h-full bg-n-surface-1 overflow-hidden">
    <header class="sticky top-0 z-10 px-6 lg:px-16 bg-n-surface-1">
      <div
        class="flex items-center justify-between w-full py-6 gap-4 mx-auto max-w-7xl"
      >
        <Breadcrumb
          :items="[
            { label: t('FLOW_BUILDER.REPORTS.BREADCRUMB_FLOWS') },
            { label: flowName },
            { label: t('FLOW_BUILDER.REPORTS.BREADCRUMB_REPORTS') },
          ]"
          @click="onBreadcrumbClick"
        />
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          placement="bottom-right"
          @date-range-changed="onDateRangeChange"
        />
      </div>
      <div class="max-w-7xl mx-auto">
        <woot-tabs
          class="[&_ul]:p-0 top-px relative"
          :index="selectedTabIndex"
          border
          @change="onTabChange"
        >
          <woot-tabs-item
            v-for="(tab, index) in TABS"
            :key="tab.key"
            :index="index"
            :name="tab.name"
            :show-badge="false"
            is-compact
          />
        </woot-tabs>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto scrollbar-custom px-6 lg:px-16">
      <div class="w-full mx-auto max-w-7xl py-6">
        <component
          :is="activeComponent"
          :report="store.report"
          :flow-id="flowId"
          :loading="store.reportLoading && !store.report"
        />
      </div>
    </main>
  </section>
</template>
